import { useNavigation } from "@react-navigation/native";
import React, { useEffect, useState } from "react";
import {
  View,
  ActivityIndicator,
  FlatList,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
} from "react-native";
import { SafeAreaView } from 'react-native-safe-area-context';

import { DSText } from '../../design-system/components/DSText';
import { useAppTheme } from '../../design-system/ThemeProvider';
import { spacing, radius, shadows } from '../../design-system/tokens';
import { useFamilyData } from "../../features/groups/useFamilyData";
import { useLanguageStore } from '../../features/quiz-core/languageStore';
import { useUserProfile } from "../../features/quiz-core/useStore";
import { t } from '../../i18n/translations';
import {
  fetchFamilyLeaderboardPaged,
  fetchFriendsLeaderboardPaged,
} from "../services/leaderboardService";

import LeaderboardEntryCard from "./LeaderboardEntryCard";


type LeaderboardScope = "family" | "friends";
type LeaderboardTimeframe = "alltime" | "weekly";

interface LeaderboardEntry {
  user_id: string;
  name?: string;
  display_name?: string;
  username?: string;
  user_profiles?: {
    display_name?: string;
    username?: string;
  };
  avatar_url?: string;
  total_score?: number;
  weekly_score?: number;
  level?: number;
  title?: string;
  rank?: number;
}

const LeaderboardScreen: React.FC = () => {
  const { colors } = useAppTheme();
  const { language } = useLanguageStore();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- cross-navigator navigation requires untyped nav
  const navigation = useNavigation<any>();
  const userProfile = useUserProfile();

  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scope, setScope] = useState<LeaderboardScope>("friends");
  const [timeframe, setTimeframe] = useState<LeaderboardTimeframe>("alltime");

  const { families, loading: loadingFamilies } = useFamilyData(userProfile.id);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      if (!userProfile.id) return;

      setLoading(true);
      setError(null);
      try {
        let data: LeaderboardEntry[] = [];
        const limit = 50;
        const offset = 0;

        switch (scope) {
          case "family":
            if (families && families.length > 0) {
              data = await fetchFamilyLeaderboardPaged(
                families[0].id,
                timeframe,
                limit,
                offset
              );
            }
            break;
          case "friends":
            data = await fetchFriendsLeaderboardPaged(
              userProfile.id,
              timeframe,
              limit,
              offset
            );
            break;
        }
        setEntries(data || []);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        if (message.includes('does not exist')) {
          // Treat missing view/table as just no data yet
          setEntries([]);
          setError(null);
        } else {
          setError(message || 'Error loading leaderboard');
        }
      } finally {
        setLoading(false);
      }
    };

    if (!loadingFamilies) {
      fetchLeaderboard();
    }
  }, [scope, timeframe, userProfile.id, families, loadingFamilies]);

  const renderToggleButton = (
    target: "scope" | "timeframe",
    value: LeaderboardScope | LeaderboardTimeframe,
    label: string,
    disabled: boolean = false
  ) => {
    const isActive = target === "scope" ? scope === value : timeframe === value;
    const onPress = () => {
      if (disabled) return;
      if (target === "scope") {
        setScope(value as LeaderboardScope);
      } else {
        setTimeframe(value as LeaderboardTimeframe);
      }
    };

    return (
      <TouchableOpacity
        key={value}
        style={[
          {
            paddingVertical: spacing.md,
            paddingHorizontal: spacing.xl,
            borderRadius: radius.md,
            borderWidth: 2,
            borderColor: isActive ? colors.primary : colors.outlineVariant,
            backgroundColor: isActive ? colors.primary : colors.surfaceContainerLow,
            ...((isActive ? shadows.md : shadows.sm) as object),
          },
          disabled && { opacity: 0.5 },
        ]}
        onPress={onPress}
        disabled={disabled}
      >
        <DSText
          variant="label"
          color={isActive ? colors.onPrimary : colors.onSurface}
          align="center"
          style={{ fontWeight: isActive ? '700' : '600' }}
        >
          {label}
        </DSText>
      </TouchableOpacity>
    );
  };

  if (loading || loadingFamilies) {
    return (
      <View style={staticStyles.centered}>
        <ActivityIndicator size="large" color={colors.primary} />
        <DSText variant="body" color={colors.onSurfaceVariant}>
          {t('leaderboard.loading', language)}
        </DSText>
      </View>
    );
  }

  // Show error only if it's not a missing view error we already handled
  if (error) {
    return (
      <View style={staticStyles.centered}>
        <DSText variant="body" color={colors.incorrect} style={{ fontWeight: 'bold' }}>
          {error}
        </DSText>
      </View>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.surface }}>
      <ScrollView
        style={{ flex: 1, paddingHorizontal: spacing['2xl'] }}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* Premium Header Card */}
        <View style={{
          borderRadius: radius.xl,
          padding: spacing['3xl'],
          marginBottom: spacing['2xl'],
          marginTop: spacing['2xl'],
          alignItems: 'center',
          backgroundColor: colors.surface,
          borderWidth: 2,
          borderColor: colors.outlineVariant,
          ...(shadows.lg as object),
        }}>
          <View style={{
            borderRadius: radius.xl,
            padding: spacing['2xl'],
            marginBottom: spacing['2xl'],
            backgroundColor: colors.primary,
            ...(shadows.md as object),
          }}>
            <DSText variant="display" color={colors.onPrimary} style={{ fontSize: 48 }}>
              {"🌱"}
            </DSText>
          </View>

          <DSText variant="display" color={colors.onSurface} align="center" style={{ marginBottom: spacing.sm }}>
            {t('leaderboard.header', language)}
          </DSText>
          <DSText variant="body" color={colors.onSurfaceVariant} align="center">
            {t('leaderboard.subtitle', language)}
          </DSText>
        </View>

        {/* Premium Toggle Cards */}
        <View style={{
          borderRadius: radius.lg,
          padding: spacing['2xl'],
          marginBottom: spacing['2xl'],
          backgroundColor: colors.surface,
          borderWidth: 2,
          borderColor: colors.outlineVariant,
          ...(shadows.md as object),
        }}>
          <DSText
            variant="title"
            color={colors.onSurface}
            align="center"
            style={{ marginBottom: spacing.lg }}
          >
            {t('leaderboard.scope', language)}
          </DSText>
          <View style={{ flexDirection: 'row', justifyContent: 'center', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.xl }}>
            {renderToggleButton('scope', 'friends', t('leaderboard.friends', language))}
            {renderToggleButton('scope', 'family', t('leaderboard.family', language), families.length === 0)}
          </View>

          <DSText
            variant="title"
            color={colors.onSurface}
            align="center"
            style={{ marginBottom: spacing.lg }}
          >
            {t('leaderboard.timePeriod', language)}
          </DSText>
          <View style={{ flexDirection: 'row', justifyContent: 'center', gap: spacing.sm }}>
            {renderToggleButton('timeframe', 'alltime', t('leaderboard.alltime', language))}
            {renderToggleButton('timeframe', 'weekly', t('leaderboard.weekly', language))}
          </View>
        </View>

        <FlatList
          style={{ width: "100%", marginTop: spacing.xl }}
          data={entries}
          keyExtractor={(item) => item.user_id}
          renderItem={({ item }) => (
            <TouchableOpacity onPress={() => navigation.navigate('Me' as never, { screen: 'ProfileDetail', params: { userId: item.user_id } })}>
              <LeaderboardEntryCard
                name={item.username || item.display_name || item.user_profiles?.username || item.user_profiles?.display_name || item.name || t('leaderboard.anonymous', language)}
                avatar_url={item.avatar_url}
                title={item.title}
                level={item.level}
                xp={item.total_score ?? 0}
                rank={item.rank ?? 0}
                isUser={item.user_id === userProfile.id}
              />
            </TouchableOpacity>
          )}
          contentContainerStyle={staticStyles.list}
          showsVerticalScrollIndicator={false}
          ListEmptyComponent={
            <View style={staticStyles.centered}>
              <DSText variant="title" color={colors.onSurfaceVariant}>
                {t('leaderboard.noScoresYet', language)}
              </DSText>
            </View>
          }
        />
      </ScrollView>
    </SafeAreaView>
  );
};

const staticStyles = StyleSheet.create({
  list: { paddingBottom: spacing['2xl'], gap: spacing.lg },
  centered: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: spacing['3xl'],
  },
});

export default LeaderboardScreen;
